//#region react import
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
//#endregion

//#region component import
import FormationMachineList from "../../../Components/FormationMachineList/FormationMachineList.component";
import GstaSimpleTable from "../../../Components/GstaSimpleTable/GstaSimpleTable.component";
import PageWithTitle from "../../../Components/PageWithTitle/PageWithTitle.component";
import SwitchWithText from "../../../Components/SwitchWithText/SwitchWithText.component";
import ScrollToTopController from "../../../Helper/CustomHook/ScrollToTopController";
import CreateFormationForm from "../../../Pages/Center/CreateFormationForm/CreateFormationForm.component";
import EditDeleteActions from "../EditDeleteActions/EditDeleteActions.component";
import SettingSwitch from "../SettingSwitch/SettingSwitch.component";
//#endregion

//#region functions import
import { getTranslations } from "../../../Helper/TranslationController";
//#endregion

//#region service import
import {
  deleteCustomTrainingGroup,
  getFormations,
  updateArchiveCustomTrainingGroup,
} from "../../../Services/SessionsService";
import { setCenterSettings } from "../../../Services/SettingsService";
//#endregion

//#region store import
import { setErrorNotification, setSuccessNotification } from "../../../ReduxStore/notificationSlice";
import { setSetting } from "../../../ReduxStore/settingSlice";
//#endregion

//#region constants import
import { NONE } from "../../../Constants/HeaderType";
import { ICON_ADD_CIRCLE, ICON_CONVERSION_PATH, ICON_IA } from "../../../Constants/IconConstants";
import { TRAINING_IA_ACTIVATION } from "../../../Constants/SettingsConstants";
import { TRANSLATIONS } from "../../../Constants/Translations";
//#endregion

//#region style import
import "./FormationPage.style.scss";
//#endregion

const FormationPage = () => {
  //#region store hooks
  const dispatch = useDispatch();
  const centerId = useSelector((state) => state.connexionSlice.trainer?.activeCenter?.id);
  const translations = useSelector((state) => state.translationSlice.translations);
  const settingDictionary = useSelector((state) => state.settingSlice.settingDictionary);
  //#endregion

  //#region state hooks
  const [isCreateTrainingModalOpen, setIsCreateTrainingModalOpen] = useState(false);
  const [selectedFormationId, setSelectedFormationId] = useState(null);
  //#endregion

  //#region useQuery
  const { data: formations, refetch: refetchFormations } = useQuery({
    queryKey: ["formations", centerId],
    queryFn: () => getFormations(centerId, true, true),
    enabled: !!centerId,
  });

  const { mutate: deleteCustomTrainingGroupMutate } = useMutation({
    mutationFn: (training) => deleteCustomTrainingGroup(centerId, training.id),
    onSuccess: () => {
      refetchFormations();
      dispatch(setSuccessNotification(getTranslations(TRANSLATIONS.CUSTOM_TRAINING_DELETED_CONFIRM, translations)));
    },
    onError: (error) => {
      dispatch(setErrorNotification(error));
    },
  });

  const { mutate: updateArchiveCustomTrainingGroupMutate } = useMutation({
    mutationFn: ({ trainingId, isArchived }) => updateArchiveCustomTrainingGroup(centerId, trainingId, isArchived),
    onSuccess: () => {
      refetchFormations();
      dispatch(setSuccessNotification(getTranslations(TRANSLATIONS.CUSTOM_TRAINING_UPDATED_CONFIRM, translations)));
    },
    onError: (error) => {
      dispatch(setErrorNotification(error));
    },
  });
  //#endregion

  //#region functions
  const handleChangeTrainingIAActivation = async () => {
    try {
      const newValue = await setCenterSettings(
        centerId,
        settingDictionary[TRAINING_IA_ACTIVATION] !== "true",
        TRAINING_IA_ACTIVATION
      );
      dispatch(setSetting({ settingName: TRAINING_IA_ACTIVATION, settingValue: newValue.dataModified }));
    } catch (e) {
      dispatch(setErrorNotification(e));
    }
  };

  const openTrainingEditingModal = (value) => {
    setSelectedFormationId(value.id);
    setIsCreateTrainingModalOpen(true);
  };
  //#endregion

  //#region constants
  const headerDefinition = [
    {
      columnName: getTranslations(TRANSLATIONS.GSTA_FORMATION_NAME, translations),
      type: NONE,
      getValue: (value) => getTranslations(value.name, translations),
      expandedColumn: false,
      searchColumn: false,
      width: "16%",
    },
    {
      columnName: getTranslations(TRANSLATIONS.GSTA_VEHICLES_SELECTED, translations),
      type: NONE,
      overrideColumn: FormationMachineList,
      width: "16%",
    },
    {
      columnName: getTranslations(TRANSLATIONS.GSTA_FORMATION_DISPLAY, translations),
      type: NONE,
      overrideColumn: SwitchWithText,
      overrideProps: {
        overrideStyleClass: "formation-display-switch",
        getValue: (value) => !value.isArchived,
        onText: getTranslations(TRANSLATIONS.COMMON_YES, translations),
        offText: getTranslations(TRANSLATIONS.COMMON_NO, translations),
        handleChange: (value) => {
          updateArchiveCustomTrainingGroupMutate({ trainingId: value.id, isArchived: !value.isArchived });
        },
      },
      width: "16%",
    },
  ];
  //#endregion
  return (
    <PageWithTitle title={getTranslations(TRANSLATIONS.GSTA_TITLE_CENTER_FORMATIONS, translations)}>
      <SettingSwitch
        setting={TRAINING_IA_ACTIVATION}
        title={getTranslations(TRANSLATIONS.TRAINING_IA_NAME_KEY, translations)}
        label={getTranslations(TRANSLATIONS.ALLOW_TRAINEES_IA_SUBSCRIPTION, translations)}
        updateSettingValue={handleChangeTrainingIAActivation}
        titleIcon={ICON_IA}
      />
      <ScrollToTopController />
      <section className="manage-inscription">
        <header>
          <h1>
            <i className={ICON_CONVERSION_PATH} />
            <span>{getTranslations(TRANSLATIONS.GSTA_FORMATION_CUSTOMIZATION, translations)}</span>
          </h1>
          <button
            className="gsta-button-outlined"
            onClick={() => {
              setSelectedFormationId(null);
              setIsCreateTrainingModalOpen(true);
            }}
          >
            <i className={ICON_ADD_CIRCLE} />
            <span>{getTranslations(TRANSLATIONS.GSTA_BUTTON_FORMATION_ADD, translations)}</span>
          </button>
        </header>
        <CreateFormationForm
          modalOpen={isCreateTrainingModalOpen}
          closeModal={() => {
            setIsCreateTrainingModalOpen(false);
            setSelectedFormationId(null);
          }}
          refetchFormations={refetchFormations}
          formationId={selectedFormationId}
        />
        <GstaSimpleTable
          headerDefinitions={headerDefinition}
          values={formations ?? []}
          Actions={EditDeleteActions}
          ActionsProps={{
            deleteAction: deleteCustomTrainingGroupMutate,
            openModalForEditing: openTrainingEditingModal,
            title: getTranslations(TRANSLATIONS.GSTA_DELETE_TRAINING, translations),
            messages: [
              getTranslations(TRANSLATIONS.GSTA_ABOUT_TO_DELETE_TRAINING, translations),
              getTranslations(TRANSLATIONS.GSTA_ARCHIVED_TRAINING_AND_STUDENT, translations),
              getTranslations(TRANSLATIONS.GSTA_WOULD_YOU_REALLY_DELETE_TRAINING, translations),
            ],
            isVisible: (formation) => !formation.isAcreos,
          }}
          overrideClass={"custom-field_table"}
          noResultPlaceHolder={getTranslations(TRANSLATIONS.NO_FORMATION_ON_CENTER, translations)}
        />
      </section>
    </PageWithTitle>
  );
};

export default FormationPage;
